const BRAND_THEME = Object.freeze({
  light: {
    brand: '#8B5CF6',
    homePageBg: '#f8f7fb',
    fullscreenHeaderBg: '#f8f7fb',
    safeAreaBg: '#f8f7fb',
    brandTop: '#f8f7fb',
    homeChrome: '#f8f7fb',
    text: '#111827',
    hint: '#6b7280',
    link: '#8B5CF6',
    button: '#8B5CF6',
  },
  dark: {
    brand: '#8B5CF6',
    homePageBg: '#111827',
    fullscreenHeaderBg: '#111827',
    brandTop: '#111827',
    homeChrome: '#111827',
    text: '#f3f4f6',
    hint: '#9ca3af',
    link: '#a78bfa',
    button: '#8B5CF6',
  },
})

function normalizeHexColor(value) {
  if (typeof value !== 'string') return null
  const raw = value.trim().replace(/^#/, '')
  if (/^[0-9a-fA-F]{6}$/.test(raw)) {
    return `#${raw.toLowerCase()}`
  }
  if (/^[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw.split('').map((char) => char + char).join('').toLowerCase()}`
  }
  return null
}

function resolveIsDark(isDark) {
  if (typeof isDark === 'boolean') return isDark
  return document.documentElement.classList.contains('dark')
}

/**
 * Sync Telegram theme params and brand defaults to CSS variables used across the app.
 */
export function syncTelegramThemeCssVars(options = {}) {
  const isDark = resolveIsDark(options.isDark)
  const defaults = isDark ? BRAND_THEME.dark : BRAND_THEME.light
  const params = window.Telegram?.WebApp?.themeParams || {}
  const root = document.documentElement

  const themeBg = normalizeHexColor(params.bg_color) || defaults.homePageBg
  const themeSecondaryBg = normalizeHexColor(params.secondary_bg_color) || themeBg
  const themeHeaderBg = normalizeHexColor(params.header_bg_color) || themeSecondaryBg
  const themeText = normalizeHexColor(params.text_color) || defaults.text
  const themeHint = normalizeHexColor(params.hint_color) || defaults.hint
  const themeLink = normalizeHexColor(params.link_color) || defaults.link
  const themeButton = normalizeHexColor(params.button_color) || defaults.button

  const homePageBg = isDark ? themeBg : defaults.homePageBg
  const fullscreenHeaderBg = isDark ? defaults.fullscreenHeaderBg : defaults.homePageBg
  const safeAreaBg = isDark ? defaults.brandTop : defaults.safeAreaBg
  const brandTopColor = safeAreaBg
  const homeChromeColor = isDark ? defaults.homeChrome : defaults.homeChrome

  root.style.setProperty('--tg-theme-bg', themeBg)
  root.style.setProperty('--tg-theme-secondary-bg', themeSecondaryBg)
  root.style.setProperty('--tg-theme-header-bg', themeHeaderBg)
  root.style.setProperty('--tg-theme-text', themeText)
  root.style.setProperty('--tg-theme-hint', themeHint)
  root.style.setProperty('--tg-theme-link', themeLink)
  root.style.setProperty('--tg-theme-button', themeButton)
  root.style.setProperty('--tg-brand-color', defaults.brand)
  root.style.setProperty('--tg-home-page-bg', homePageBg)
  root.style.setProperty('--tg-fullscreen-header-bg', fullscreenHeaderBg)
  root.style.setProperty('--tg-safe-area-bg', safeAreaBg)
  root.style.setProperty('--tg-brand-top-color', brandTopColor)
  root.style.setProperty('--tg-home-chrome-color', homeChromeColor)

  // Legacy aliases used in tailwind.css / HomeView styles
  root.style.setProperty('--brand-top-color', brandTopColor)
  root.style.setProperty('--fullscreen-header-bg', fullscreenHeaderBg)
  root.style.setProperty('--home-page-bg', homePageBg)
}

export function getTelegramHomePageBg(isDark = resolveIsDark()) {
  const root = document.documentElement
  const cssValue = getComputedStyle(root).getPropertyValue('--tg-home-page-bg').trim()
  if (cssValue) return cssValue
  return isDark ? BRAND_THEME.dark.homePageBg : BRAND_THEME.light.homePageBg
}

export function getTelegramHomeChromeColor(isDark = resolveIsDark()) {
  const root = document.documentElement
  const cssValue = getComputedStyle(root).getPropertyValue('--tg-home-chrome-color').trim()
  if (cssValue) return cssValue
  return isDark ? BRAND_THEME.dark.homeChrome : BRAND_THEME.light.homeChrome
}

export function getTelegramChromeBackgroundColor(isDark = resolveIsDark()) {
  return getTelegramHomePageBg(isDark)
}
